import { useCallback, useRef, useState } from 'react';
import {
  clientSearchService,
  type ClientSearchOptions,
  type ClientSearchResult,
} from '../services/clientService';

interface UseClientSearchOptions extends ClientSearchOptions {
  debounceMs?: number;
}

interface UseClientSearchReturn {
  suggestions: ClientSearchResult[];
  isLoading: boolean;
  error: string | null;
  searchClients: (query: string) => void;
  clearSuggestions: () => void;
}

/**
 * Hook pour la recherche intelligente de clients avec debounce
 */
export const useClientSearch = (options: UseClientSearchOptions = {}): UseClientSearchReturn => {
  const { limit = 3, searchFields = ['full_name', 'email', 'phone'], debounceMs = 300 } = options;

  const [suggestions, setSuggestions] = useState<ClientSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Référence pour le timeout de debounce
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Référence pour annuler les requêtes en cours
  const abortControllerRef = useRef<AbortController | null>(null);

  const searchClients = useCallback(
    async (query: string) => {
      // Annuler la requête précédente si elle existe
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Annuler le timeout précédent
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Si la recherche est vide, vider les suggestions
      if (!query || query.trim().length < 1) {
        setSuggestions([]);
        setIsLoading(false);
        setError(null);
        return;
      }

      // Créer un nouveau controller pour cette requête
      abortControllerRef.current = new AbortController();

      // Debounce la recherche
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          setIsLoading(true);
          setError(null);

          const results = await clientSearchService.searchClients(query, {
            limit,
            searchFields,
          });

          // Vérifier si la requête n'a pas été annulée
          if (!abortControllerRef.current?.signal.aborted) {
            setSuggestions(results);
          }
        } catch (err) {
          // Vérifier si l'erreur n'est pas due à une annulation
          if (!abortControllerRef.current?.signal.aborted) {
            const errorMessage = err instanceof Error ? err.message : 'Erreur lors de la recherche';
            setError(errorMessage);
            setSuggestions([]);
            console.error('Erreur de recherche de clients:', err);
          }
        } finally {
          if (!abortControllerRef.current?.signal.aborted) {
            setIsLoading(false);
          }
        }
      }, debounceMs);
    },
    [limit, searchFields, debounceMs]
  );

  const clearSuggestions = useCallback(() => {
    // Annuler toute requête en cours
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Annuler le timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    setSuggestions([]);
    setIsLoading(false);
    setError(null);
  }, []);

  return {
    suggestions,
    isLoading,
    error,
    searchClients,
    clearSuggestions,
  };
};
